import { useState, useCallback, useEffect, RefObject } from "react";

interface MasonryItem {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseMasonryOptions {
  columnWidth?: number;
}

const getGapFromContainer = (container: HTMLElement): number => {
  const gapValue = getComputedStyle(container).getPropertyValue("--masonry-gap");
  if (!gapValue) return 16;
  const temp = document.createElement("div");
  temp.style.width = gapValue;
  container.appendChild(temp);
  const px = temp.offsetWidth;
  container.removeChild(temp);
  return px;
};

export const useMasonry = (
  containerRef: RefObject<HTMLDivElement | null>,
  itemCount: number,
  options: UseMasonryOptions = {}
) => {
  const { columnWidth = 170 } = options;
  const [gap, setGap] = useState(16);

  const [positions, setPositions] = useState<MasonryItem[]>([]);
  const [containerHeight, setContainerHeight] = useState(0);
  const [itemHeights, setItemHeights] = useState<Map<number, number>>(new Map());

  const registerItemHeight = useCallback((index: number, height: number) => {
    setItemHeights((prev) => {
      if (prev.get(index) === height) return prev;
      const next = new Map(prev);
      next.set(index, height);
      return next;
    });
  }, []);

  const calculateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container || itemCount === 0) {
      setPositions([]);
      setContainerHeight(0);
      return;
    }

    const containerWidth = container.offsetWidth;
    const columnCount = Math.max(1, Math.floor((containerWidth + gap) / (columnWidth + gap)));
    const actualColumnWidth = (containerWidth - gap * (columnCount - 1)) / columnCount;

    const columnHeights = new Array(columnCount).fill(0);
    const newPositions: MasonryItem[] = [];

    for (let i = 0; i < itemCount; i++) {
      const shortestColumn = columnHeights.indexOf(Math.min(...columnHeights));
      const x = shortestColumn * (actualColumnWidth + gap);
      const y = columnHeights[shortestColumn];

      const itemHeight = itemHeights.get(i) ?? actualColumnWidth;

      newPositions.push({
        index: i,
        x,
        y,
        width: actualColumnWidth,
        height: itemHeight,
      });

      columnHeights[shortestColumn] += itemHeight + gap;
    }

    setPositions(newPositions);
    setContainerHeight(Math.max(...columnHeights) - gap);
  }, [containerRef, itemCount, itemHeights, gap, columnWidth]);

  useEffect(() => {
    calculateLayout();
  }, [calculateLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setGap(getGapFromContainer(container));
      calculateLayout();
    });

    setGap(getGapFromContainer(container));
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [containerRef, calculateLayout]);

  return {
    positions,
    containerHeight,
    registerItemHeight,
    recalculate: calculateLayout,
    loadedItemCount: itemHeights.size,
  };
};
